import {z} from 'zod';

export enum InviteStatus {
  Unknown = '',
  Valid = 'VALID',
  HouseholdFull = 'HOUSEHOLD_FULL',
}

export const inviteSchema = z.object({
  code: z.string().min(1, 'Invite code is required'),
  inviteeEmail: z.string().email('Invalid email address'),
  inviteeName: z.string().min(1, 'Invitee name is required'),
  referrerName: z.string().min(1, 'Referrer name is required'),
  status: z.nativeEnum(InviteStatus),
});

export type Invite = z.infer<typeof inviteSchema>;

export function isValidInvite(value: unknown): value is Invite {
  if (typeof value !== 'object' || value === null) return false;

  const invite = value as Invite;

  return (
    typeof invite.code === 'string' &&
    typeof invite.inviteeEmail === 'string' &&
    typeof invite.inviteeName === 'string' &&
    typeof invite.referrerName === 'string' &&
    Object.values(InviteStatus).includes(invite.status as InviteStatus)
  );
}

/**
 * Get the invite data from the search params.
 * @param searchParams A URLSearchParams object or a Record<string, string | string[] | undefined> (as the searchParams prop of a Next.js Page)
 * @returns An {@link Invite} object or null if the invite data is not present in the search params.
 */
export function getInviteFromSearchParams(
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>
) {
  const params: Record<string, string | null> = {};

  if (searchParams instanceof URLSearchParams) {
    searchParams.forEach((value, key) => {
      params[key] = value ?? null;
    });
  } else {
    for (const [key, value] of Object.entries(searchParams)) {
      params[key] = (value as string) ?? null;
    }
  }

  const code = params['c'] ?? params['code'] ?? '';
  const inviteeEmail = fixInviteeEmail(params['i'] ?? params['id'] ?? '');
  const inviteeName = params['n'] ?? '';
  const referrerName = params['r'] ?? '';
  const status = params['s'] ?? '';

  if (code === '' || inviteeEmail === '' || inviteeName === '' || referrerName === '') {
    return null;
  }

  const result = inviteSchema.safeParse({
    code,
    inviteeEmail,
    inviteeName,
    referrerName,
    status,
  });
  if (!result.success) {
    console.warn(
      `Failed to parse invite data: ${JSON.stringify(result.error.errors)} in ${JSON.stringify(params)}`
    );
    return null;
  }

  return result.data;
}

function fixInviteeEmail(inviteeEmail: string | null) {
  if (inviteeEmail == null) {
    return '';
  }

  // The Loyalty platform doesn't encode any '+' characters in the email address. Browsers treat '+' as a space, so assume that any spaces are actually '+' characters.
  return inviteeEmail.replaceAll(' ', '+');
}
